import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { ROLES } from "../../../lib/roles";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../../components/ui/dialog";
import { Loader2 } from "lucide-react";

const ESTADOS = [
    "ratificada", "disponible", "dispuesta",
    "amortizando", "pagada", "en_default", "reestructurada",
];

function money(n, cur = "MXN") {
    return Number(n || 0).toLocaleString("es-MX", { style: "currency", currency: cur });
}

export default function OverviewTab({ facility, reload }) {
    const { hasRole } = useAuth();
    const [cortes, setCortes] = useState([]);
    const [openEstado, setOpenEstado] = useState(false);
    const [nuevoEstado, setNuevoEstado] = useState(facility.estado);
    const [motivo, setMotivo] = useState("");
    const [busy, setBusy] = useState(false);

    const canEdit = hasRole([ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.JURIDICO, ROLES.ADMIN_TI]);

    useEffect(() => {
        api.get("/tape/cortes").then(({ data }) => {
            setCortes((data || []).filter((c) => c.facility_id === facility.id));
        }).catch(() => {});
    }, [facility.id]);

    const cambiarEstado = async () => {
        if (!motivo.trim()) return toast.error("Motivo requerido");
        setBusy(true);
        try {
            await api.post(`/facilities/${facility.id}/estado`, null, {
                params: { nuevo_estado: nuevoEstado, motivo },
            });
            toast.success(`Estado → ${nuevoEstado}`);
            setOpenEstado(false);
            setMotivo("");
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="tab-overview-content">
            <div className="lg:col-span-2 rounded-md border border-border bg-card p-5 space-y-4">
                <h3 className="font-display text-xl">Detalles contractuales</h3>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <div>
                        <dt className="text-[10px] uppercase tracking-widest text-muted-foreground">Tipo</dt>
                        <dd>{facility.tipo}</dd>
                    </div>
                    <div>
                        <dt className="text-[10px] uppercase tracking-widest text-muted-foreground">Amortización</dt>
                        <dd>{facility.amortizacion}</dd>
                    </div>
                    <div>
                        <dt className="text-[10px] uppercase tracking-widest text-muted-foreground">Tasa base</dt>
                        <dd>{facility.tasa_base} + {facility.spread_pct}%</dd>
                    </div>
                    <div>
                        <dt className="text-[10px] uppercase tracking-widest text-muted-foreground">All-in</dt>
                        <dd className="tabular">{facility.all_in_pct}%</dd>
                    </div>
                    <div>
                        <dt className="text-[10px] uppercase tracking-widest text-muted-foreground">Fecha efectiva</dt>
                        <dd className="tabular">{facility.fecha_efectiva}</dd>
                    </div>
                    <div>
                        <dt className="text-[10px] uppercase tracking-widest text-muted-foreground">Vencimiento</dt>
                        <dd className="tabular">{facility.fecha_vencimiento}</dd>
                    </div>
                    <div>
                        <dt className="text-[10px] uppercase tracking-widest text-muted-foreground">Lender principal</dt>
                        <dd className="hash-cell">{facility.lender_principal_persona_id}</dd>
                    </div>
                    <div>
                        <dt className="text-[10px] uppercase tracking-widest text-muted-foreground">Co-lenders</dt>
                        <dd>{facility.colenders?.length || 0}</dd>
                    </div>
                </dl>
                {facility.notas && (
                    <div className="pt-3 border-t border-border">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Notas</div>
                        <div className="text-sm text-muted-foreground">{facility.notas}</div>
                    </div>
                )}
            </div>

            <div className="rounded-md border border-border bg-card p-5 space-y-3">
                <h3 className="font-display text-lg">Ciclo de vida</h3>
                <div className="flex items-center gap-2">
                    <span className="badge-gold">{facility.estado}</span>
                    {canEdit && (
                        <Dialog open={openEstado} onOpenChange={setOpenEstado}>
                            <DialogTrigger asChild>
                                <Button size="sm" variant="outline" data-testid="cambiar-estado-btn">Cambiar</Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Cambio de estado</DialogTitle></DialogHeader>
                                <div className="space-y-3">
                                    <div>
                                        <Label>Nuevo estado</Label>
                                        <Select value={nuevoEstado} onValueChange={setNuevoEstado}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {ESTADOS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Motivo</Label>
                                        <Input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                                            data-testid="motivo-cambio-estado" />
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button onClick={cambiarEstado} disabled={busy} data-testid="submit-cambio-estado">
                                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aplicar"}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>
                <div className="pt-3 border-t border-border">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                        Cortes de tape ({cortes.length})
                    </div>
                    {cortes.length === 0 && (
                        <div className="text-xs text-muted-foreground">Sin cortes de cartera vinculados aún.</div>
                    )}
                    <ul className="space-y-1 text-xs">
                        {cortes.slice(0, 5).map((c) => (
                            <li key={c.corte_id} className="flex justify-between border-b border-border/50 pb-1">
                                <span className="hash-cell">{c.corte_id?.slice(0, 12)}…</span>
                                <span className="tabular">{c.fecha_corte}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="pt-3 border-t border-border text-[11px] text-muted-foreground">
                    Comprometido: <span className="tabular font-medium">{money(facility.monto_comprometido, facility.moneda)}</span><br/>
                    Dispuesto: <span className="tabular font-medium">{money(facility.monto_dispuesto, facility.moneda)}</span>
                </div>
            </div>
        </div>
    );
}
